// src/features/tasks/components/TaskDetailsPage/TaskManualsCard.jsx
// The task's manuals, shown inline (PDFs, images and videos render in the card) so the
// person doing the task can read them without opening anything. With several
// manuals a tab row switches between them. Renders nothing but its dialogs
// while the task has no manuals; the page offers "Tilføj manual" instead.
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  InsertDriveFileOutlined,
  LinkOff as LinkOffIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTaskManuals } from '../../hooks/useTaskManuals';
import { useTaskPermissions } from '../../../auth/hooks/usePermissions';
import { useFileUrl, downloadFileLink } from '../../../files/hooks/useFileUrl';
import { FileFullscreenViewer } from '../../../files/components/FileFullscreenViewer';
import { VideoPlayer } from '../../../files/components/VideoPlayer';
import { getPreviewKind } from '../../../files/utils/previewKind';
import { AddManualsDialog } from './AddManualsDialog';
import SectionHeader from '../../../../components/common/SectionHeader';

const PREVIEW_HEIGHT = 720;

const kindOf = (file) => getPreviewKind(file?.filetype, file?.filename);

const downloadFile = (file) => downloadFileLink(file.file_id, { filename: file.filename });

function ManualPreview({ file }) {
  const { t } = useTranslation(['common', 'tasks']);
  const kind = kindOf(file);
  // Signed link: the browser streams it (video seeks, PDFs load page by page).
  // Other types get a download prompt, so no link is needed up front.
  const { url: imageUrl, loading, error } = useFileUrl(kind === 'other' ? null : file.file_id);

  const frame = {
    height: PREVIEW_HEIGHT,
    maxHeight: '75vh',
    border: 1,
    borderColor: 'divider',
    borderRadius: 2,
    overflow: 'hidden',
    bgcolor: 'background.default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (kind === 'other') {
    return (
      <Box sx={{ ...frame, height: 'auto', py: 8, flexDirection: 'column', gap: 3 }}>
        <InsertDriveFileOutlined sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          {t('tasks:manuals.no_preview')}
        </Typography>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadFile(file)}>
          {t('tasks:manuals.download')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={frame}>
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{t('tasks:manuals.load_failed')}</Alert>}
      {imageUrl && !loading && (kind === 'pdf' ? (
        <embed src={imageUrl} type="application/pdf" title={file.filename} style={{ width: '100%', height: '100%', border: 'none' }} />
      ) : kind === 'video' ? (
        <VideoPlayer src={imageUrl} title={file.filename} style={{ width: '100%', height: '100%' }} />
      ) : (
        <img src={imageUrl} alt={file.filename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      ))}
    </Box>
  );
}

export default function TaskManualsCard({ task, addOpen, onAddOpen, onAddClose, onCountChange, sx }) {
  const { t } = useTranslation(['common', 'tasks', 'files']);
  const { canModifyManuals } = useTaskPermissions();
  const { manuals, loading, listTaskManuals, linkManualToTask, unlinkManualFromTask } = useTaskManuals();
  const [loaded, setLoaded] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  useEffect(() => {
    if (!task?.task_id) return;
    listTaskManuals(task.task_id).then(() => setLoaded(true));
  }, [task?.task_id, listTaskManuals]);

  useEffect(() => {
    if (loaded) onCountChange?.(manuals.length);
  }, [loaded, manuals.length, onCountChange]);

  // Keep a valid tab selected as manuals are added/removed
  const active = manuals.find((m) => m.task_manual_id === activeId) || manuals[0];

  const handleAdd = async (fileIds) => {
    for (const fileId of fileIds) {
      await linkManualToTask(task.task_id, { file_id: fileId });
    }
    await listTaskManuals(task.task_id);
    onAddClose?.();
  };

  const handleRemove = async () => {
    setRemoveLoading(true);
    await unlinkManualFromTask(task.task_id, removing.task_manual_id);
    await listTaskManuals(task.task_id);
    setRemoveLoading(false);
    setRemoving(null);
  };

  const dialogs = (
    <>
      <AddManualsDialog
        open={Boolean(addOpen)}
        onClose={onAddClose}
        onSubmit={handleAdd}
        linkedFileIds={manuals.map((m) => m.file.file_id)}
      />
      <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)}>
        <DialogTitle>{t('tasks:manuals.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('tasks:manuals.confirmDeleteMessage', { filename: removing?.file.filename })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)} disabled={removeLoading}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={handleRemove}
            color="error"
            variant="contained"
            disabled={removeLoading}
            startIcon={removeLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {t('common:buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      {fullscreen && active && (
        <FileFullscreenViewer
          fileId={active.file.file_id}
          filename={active.file.filename}
          filetype={active.file.filetype}
          onClose={() => setFullscreen(false)}
        />
      )}
    </>
  );

  if (!active) return dialogs;

  return (
    <>
      <Card sx={sx}>
        <CardContent>
          <SectionHeader
            icon={DescriptionIcon}
            title={manuals.length > 1 ? `${t('tasks:labels.manuals')} (${manuals.length})` : t('tasks:labels.manuals')}
            actions={canModifyManuals && (
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAddOpen}>
                {t('tasks:manuals.add')}
              </Button>
            )}
          />

          {manuals.length > 1 && (
            <Tabs
              value={active.task_manual_id}
              onChange={(e, id) => setActiveId(id)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 3, minHeight: 0, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
            >
              {manuals.map((m) => (
                <Tab
                  key={m.task_manual_id}
                  value={m.task_manual_id}
                  icon={<InsertDriveFileOutlined fontSize="small" />}
                  iconPosition="start"
                  label={m.file.filename}
                />
              ))}
            </Tabs>
          )}

          {/* Toolbar for the manual on screen */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
              {active.file.filename}
            </Typography>
            {kindOf(active.file) !== 'other' && (
              <Tooltip title={t('tasks:manuals.fullscreen')}>
                <IconButton size="small" onClick={() => setFullscreen(true)}>
                  <FullscreenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('files:messages.open_new_tab')}>
              <IconButton size="small" onClick={() => window.open(`/files/view/${active.file.file_id}`, '_blank')}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canModifyManuals && (
              <Tooltip title={t('tasks:manuals.remove')}>
                <IconButton size="small" onClick={() => setRemoving(active)} disabled={loading}>
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <ManualPreview key={active.file.file_id} file={active.file} />
        </CardContent>
      </Card>
      {dialogs}
    </>
  );
}
